import {useState} from "react";
import { FaUser, FaBriefcase, FaBuilding, FaCalendarAlt, FaExclamationTriangle } from "react-icons/fa";
import { SiMaildotru } from "react-icons/si";
import { FiFileText, FiPhone, FiUser } from "react-icons/fi";
import { useRef } from "react";
import { apiService } from "../../components/Config/api.service"; 
import AvatarCircle from "../../components/Utils/AvatarCircle";
import { TAG_COLORS } from '../../components/Constantes/Colors';
import Modal from "../../components/Utils/ModalShow";

const EmployeeInfo = ({ employee, currentContract, setActiveTab, handleArchive }) => {
  const fileInputRef = useRef();
  const [modalStep, setModalStep] = useState(0); // 0: closed, 1-3: steps
  const [confirmationInput, setConfirmationInput] = useState("");

  const handleImageUpload = async (e) => {
    const file = fileInputRef.current.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    formData.append("id", employee.id);

    try {
      await apiService.saveElement("/employee/upload_image", formData, true);
      window.location.reload(); 
    } catch (err) {
      alert("Erreur lors de l'upload de l'image");
    }
  };
  const handleCloseModal = () => {
    setModalStep(0);
    setConfirmationInput("");
  };

  const handleConfirmAction = () => {
    const isArchiving = employee.active;
    const promptText = isArchiving ? employee.name.split(' ')[0] : "CONFIRMER";

    if (!isArchiving && modalStep === 2) {
      handleArchive(isArchiving);
      handleCloseModal();
      return;
    }

    if (modalStep === 3) {
      if (confirmationInput === promptText) {
        handleArchive(isArchiving);
        handleCloseModal();
      } else {
        alert("La confirmation a échoué. L'opération a été annulée.");
        handleCloseModal();
      }
    } else {
      setModalStep(modalStep + 1);
    }
  };

  const isArchiving = employee.active;
  const actionText = isArchiving ? "archiver" : "désarchiver";
  const promptText = isArchiving ? employee.name.split(' ')[0] : "CONFIRMER";

  const renderModalContent = () => {
    switch (modalStep) {
      case 1:
        return {
          title: `Confirmer ${actionText}`,
          body: `Êtes-vous sûr de vouloir ${actionText} cet employé ?`,
          footer: (
            <>
              <button className="btn" onClick={handleCloseModal}>Annuler</button>
              <button className="btn bg-[--color-primary] text-white" onClick={handleConfirmAction}>Oui, continuer</button>
            </>
          )
        };
      case 2:
        return {
          title: "Avertissement",
          body: `Cette action est importante. L'employé ${isArchiving ? 
            "ne sera plus visible dans les listes actives et son contrat sera egalement archivé" 
            : "sera de nouveau visible partout et son contrat sera egalement desarchivé"}.`,
          footer: (
            <>
              <button className="btn" onClick={handleCloseModal}>Annuler</button>
              <button className="btn btn-warning" onClick={handleConfirmAction}>Je comprends, continuer</button>
            </>
          )
        };
      case 3:
        return {
          title: "Dernière étape",
          body: (
            <div>
              <p>Pour confirmer, veuillez taper "<span className="font-bold">{promptText}</span>" dans le champ ci-dessous.</p>
              <input
                type="text"
                value={confirmationInput}
                onChange={(e) => setConfirmationInput(e.target.value)}
                className="input input-bordered w-full mt-2"
                placeholder={promptText}
              />
            </div>
          ),
          footer: (
            <>
              <button className="btn" onClick={handleCloseModal}>Annuler</button>
              <button
                className="btn btn-error"
                onClick={handleConfirmAction}
                disabled={confirmationInput !== promptText}
              >
                {actionText.charAt(0).toUpperCase() + actionText.slice(1)}
              </button>
            </>
          )
        };
      default:
        return { title: "", body: null, footer: null };
    }
  };

  const { title, body, footer } = renderModalContent();

  return (
    <div className="min-h-screen py-6 px-3 rounded-lg">
      {/* Header with profile */}
      <div className="rounded-xl mb-6">
        <div className="relative mb-4 flex items-center justify-center">
            {employee.image ? (
              <img
                src={`data:image/png;base64,${employee.image}`}
                alt="Photo profil"
                className="w-[160px] h-[160px] object-cover rounded-3xl"
              />
            ) : (
              <div className="w-[160px] h-[160px] flex items-center justify-center bg-gray-200 rounded-3xl">
                <AvatarCircle name={employee.name} size={80} />
              </div>
            )}
            <div className="absolute bottom-0 w-[160px] bg-black bg-opacity-50 rounded-b-3xl text-white text-center py-4">
              <h2 className="text-xl font-bold">{employee.name}</h2>
              <p className="text-sm">{employee.poste}</p>
            </div>
          </div>
        <div className="flex flex-col items-center">
          <div>
            <input
              type="file"
              accept="image/*"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleImageUpload}
            />
            <button
              className="mt-2 px-3 py-1 bg-gray-200 rounded"
              onClick={() => fileInputRef.current.click()}
            >
              Changer la photo
            </button>
          </div>

          <button
            onClick={() => setActiveTab("Bulletins")}
            className="py-1 px-3 bg-[--color-primary] text-white rounded-full transition-colors flex items-center gap-2 my-3"
          >
            <FiFileText /> Aperçu du bulletin
          </button>
        </div>
      </div>

      {/* Info cards grid */}
      <div className="mb-6">
        {/* Contact card */}
        <div className="py-6 px-3">
          <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <FiUser className="text-[--color-primary]" /> Informations de contact
          </h3>

          <div className="space-y-4">
            <div className="flex items-center gap-3 rounded-lg bg-gray-50">
              <div>
                <p className="text-xs text-gray-500">Téléphone</p>
                <p className="font-medium">+221 {employee.phone}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 rounded-lg bg-gray-50">
              <div>
                <p className="text-xs text-gray-500">Email</p>
                <p className="font-medium break-all ">{employee.email}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Department card */}
        <div className="py-6 px-3">
          <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <FaBuilding className="text-[--color-primary]" /> Information de la société
          </h3>          

          <div className="space-y-2">
            <div className="flex items-center gap-3 p-2 rounded-lg bg-gray-50">
              <div>
                <p className="text-xs text-gray-500">Département</p>
                <p className="font-medium">{employee.department_id?.name || "Non assigné"}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-2 rounded-lg bg-gray-50">
              <div>
                <p className="text-xs text-gray-500">Affectation</p>
                <p className="font-medium">{employee.assignment_id?.name || "Non assigné"}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-2 rounded-lg bg-gray-50">
              <div>
                <p className="text-xs text-gray-500">Manager</p>
                <p className="font-medium">
                  {employee.manager_name || "Non assigné"}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-2 rounded-lg bg-gray-50">
              <div>
                <p className="text-xs text-gray-500">Etiquettes</p>
                <p className="flex flex-wrap gap-2 mt-2">
                  {(employee.categories || []).map(cat => (
                    <span key={cat.id} 
                    style={{
                      backgroundColor: TAG_COLORS[(cat.color || 1) - 1],
                      color: "#fff"
                    }}
                    className="badge text-white px-2 py-1 rounded">
                      {cat.name}
                    </span>
                  ))}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contract card */}
      <div className="py-6 px-3">
        <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
          <FaBriefcase className="text-[--color-primary]" /> Informations du contrat
        </h3>

        <div className="gap-4">
          <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50">
            <div>
              <p className="text-xs text-gray-500">Type de contrat</p>
              <p className="font-medium">{currentContract ? currentContract.Ref : "Aucun contrat en cours"}</p>
            </div>
          </div>

          <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50">

            <div>
              <p className="text-xs text-gray-500">Date de début</p>
              <p className="font-medium">{currentContract ? currentContract.DateStart : "-"}</p>
            </div>
          </div>

          <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50">

            <div>
              <p className="text-xs text-gray-500">Date de fin</p>
              <p className="font-medium">{currentContract ? currentContract.DateEnd : "-"}</p>
            </div>
          </div>
        </div>
      </div>
      {/* Archive Section */}
      <div className="mt-auto pt-6 px-3 text-center">
          <button 
            onClick={() => setModalStep(1)}
            className={`text-xs flex items-center justify-center gap-2 w-full text-center ${employee.active ? 'text-red-500 hover:text-red-700' : 'text-green-500 hover:text-green-700'}`}
          >
            <FaExclamationTriangle />
            {employee.active ? "Archiver l'employé" : "Réactiver l'employé"}
          </button>
      </div>
      <Modal
        isOpen={modalStep > 0}
        onClose={handleCloseModal}
        title={title}
        footer={footer}
      >
        {body}
      </Modal>
    </div>
  );
};

export default EmployeeInfo;